import type { Sale } from "../api/types";

/**
 * G-141 (OD-215): how a sale was paid, as Sales records shows it. Every euro
 * lives in exactly one place — the sale's own payment rows (the till, the
 * builder's Sell, the office "Add a sale"), the order's ledger (a sale that
 * completed a customer order) or the web checkout (a Shopify order) — and the
 * SERVER says which (`moneyHome`, `src/modules/sales/sale-figures.ts`).
 *
 * G-169 (WO-D): the sums that lived here (`sumSalePayments`, the difference
 * from the suggested price) are the server's now — `GET /sales/totals` and
 * `suggestedDifferenceEur` on each sale. What is left is words and formatting.
 */
type PaidSale = Pick<Sale, "payments" | "customerOrder" | "shopifyOrder" | "moneyHome">;

/** How money moved, in one set of words (sales, refunds). */
export const METHOD_LABEL: Record<Sale["payments"][number]["method"], string> = {
  CASH: "Cash",
  CARD: "Card",
  ONLINE: "Online",
  BANK_TRANSFER: "Transfer",
};

/** "Cash", "Cash €20.00 · Card €20.00", "Paid on order ORD-…", "Paid online (web order)". */
export function salePaidText(sale: PaidSale, money: (eur: number) => string): string {
  switch (sale.moneyHome) {
    case "shopify":
      return sale.shopifyOrder?.orderNumber
        ? `Paid online (web order ${sale.shopifyOrder.orderNumber})`
        : "Paid online (web order)";
    case "order":
      return `Paid on order ${sale.customerOrder?.code ?? ""}`.trim();
    case "notRecorded":
      return "Not recorded";
    default:
      return sale.payments.length === 1
        ? METHOD_LABEL[sale.payments[0].method]
        : sale.payments.map((p) => `${METHOD_LABEL[p.method]} ${money(Number(p.amountEur))}`).join(" · ");
  }
}

/**
 * G-162: what refund events did to a figure, as a label: "− €x refunded",
 * or — when the period holds more removed than given (owner 2026-10-19: a
 * removed refund comes back on its own day) — "+ €x refund removed". Null
 * when nothing moved. Takes the server's figure (a decimal string or a number).
 */
export function refundNote(netRefundedEur: number | string, money: (eur: number) => string): string | null {
  const cents = Math.round(Number(netRefundedEur) * 100);
  if (cents === 0) return null;
  return cents > 0
    ? `− ${money(cents / 100)} refunded`
    : `+ ${money(-cents / 100)} refund removed`;
}
